import {
  AlignmentType,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  TextRun,
} from 'docx';

export interface NPRMQuestionForDocx {
  question_id?: number;
  question_text?: string;
  question_type?: string;
  short_answer?: string;
  context_summary?: string;
  paragraph_number?: number | string;
  page_number?: number | null;
}

export interface NPRMDocxOptions {
  docketNumber: string;
  totalQuestions?: number;
  nprmFilename?: string;
  proceedingName?: string;
  questions: NPRMQuestionForDocx[];
}

const QUESTION_TYPE_LABELS: Record<string, string> = {
  proposal_request: 'Proposal',
  tentative_conclusion: 'Tentative Conclusion',
  factual_inquiry: 'Factual Inquiry',
  alternative_inquiry: 'Alternative Inquiry',
  cost_benefit_inquiry: 'Cost / Benefit',
  general_seek_comment: 'General Comment',
  NPRM: 'NPRM',
};

const humanizeType = (raw?: string): string => {
  if (!raw) return '';
  if (QUESTION_TYPE_LABELS[raw]) return QUESTION_TYPE_LABELS[raw];
  return raw
    .split('_')
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ''))
    .join(' ');
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const buildNPRMQuestionsDocx = async (
  opts: NPRMDocxOptions,
): Promise<Blob> => {
  const { docketNumber, totalQuestions, nprmFilename, proceedingName, questions } = opts;

  const paragraphs: Paragraph[] = [];

  paragraphs.push(
    new Paragraph({
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.LEFT,
      children: [
        new TextRun({
          text: `NPRM Questions — Docket ${docketNumber}`,
          bold: true,
        }),
      ],
    }),
  );

  if (proceedingName) {
    paragraphs.push(
      new Paragraph({
        spacing: { after: 80 },
        children: [
          new TextRun({ text: proceedingName, italics: true, color: '4B5563' }),
        ],
      }),
    );
  }

  if (nprmFilename) {
    paragraphs.push(
      new Paragraph({
        spacing: { after: 80 },
        children: [
          new TextRun({ text: 'Source: ', bold: true, color: '4B5563' }),
          new TextRun({ text: nprmFilename, color: '4B5563' }),
        ],
      }),
    );
  }

  paragraphs.push(
    new Paragraph({
      spacing: { after: 240 },
      children: [
        new TextRun({
          text: `${totalQuestions ?? questions.length} question${(totalQuestions ?? questions.length) === 1 ? '' : 's'} extracted`,
          color: '6B7280',
        }),
      ],
    }),
  );

  questions.forEach((q, idx) => {
    const qNum = q.question_id ?? idx + 1;
    const topic = (q.short_answer || '').trim();
    const type = humanizeType(q.question_type);
    const para = q.paragraph_number;
    const page = q.page_number;

    // Q-number heading + topic
    paragraphs.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 240, after: 60 },
        children: [
          new TextRun({ text: `Q${qNum}`, bold: true }),
          ...(topic
            ? [
                new TextRun({ text: ' — ', color: '6B7280' }),
                new TextRun({ text: topic, color: '111827' }),
              ]
            : []),
        ],
      }),
    );

    // Meta line: type · paragraph · page
    const metaParts: string[] = [];
    if (type) metaParts.push(type);
    if (para !== undefined && para !== null && para !== '') metaParts.push(`Paragraph ${para}`);
    if (page) metaParts.push(`Page ${page}`);
    if (metaParts.length > 0) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 80 },
          children: [
            new TextRun({ text: metaParts.join(' · '), color: '6B7280', size: 18 }),
          ],
        }),
      );
    }

    // Question text
    if (q.question_text) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 100 },
          children: [
            new TextRun({ text: q.question_text, color: '111827' }),
          ],
        }),
      );
    }

    // Context summary
    if (q.context_summary) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 40 },
          children: [
            new TextRun({ text: 'Context: ', bold: true, color: '4B5563', size: 20 }),
            new TextRun({ text: q.context_summary, color: '4B5563', size: 20 }),
          ],
        }),
      );
    }
  });

  const doc = new Document({
    creator: 'CommsGenie',
    description: `NPRM questions for docket ${docketNumber}`,
    title: `NPRM Questions — Docket ${docketNumber}`,
    sections: [
      {
        properties: {},
        children: paragraphs,
      },
    ],
  });

  return Packer.toBlob(doc);
};

export const downloadNPRMQuestionsDocx = async (opts: NPRMDocxOptions): Promise<void> => {
  const blob = await buildNPRMQuestionsDocx(opts);
  const safeDocket = (opts.docketNumber || 'docket').replace(/[^a-z0-9_\-\.]/gi, '_');
  downloadBlob(blob, `nprm_questions_${safeDocket}.docx`);
};

export const NPRM_QUESTION_TYPE_LABEL = humanizeType;

// Tailwind class triples for question_type chips: [bg, border, text]
export const NPRM_QUESTION_TYPE_STYLE: Record<string, { bg: string; text: string; border: string }> = {
  proposal_request: { bg: 'bg-indigo-50', text: 'text-indigo-800', border: 'border-indigo-200' },
  tentative_conclusion: { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' },
  factual_inquiry: { bg: 'bg-sky-50', text: 'text-sky-800', border: 'border-sky-200' },
  alternative_inquiry: { bg: 'bg-fuchsia-50', text: 'text-fuchsia-800', border: 'border-fuchsia-200' },
  cost_benefit_inquiry: { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-200' },
  general_seek_comment: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' },
};

export const getNPRMQuestionTypeStyle = (type?: string) =>
  (type && NPRM_QUESTION_TYPE_STYLE[type]) || NPRM_QUESTION_TYPE_STYLE.general_seek_comment;
